import React from 'react';
import { FooterSection, BreadCrumb, StaticPageHeader } from '../components';
import Pagination from './components/Pagination';
import { StaticListIndexSingleData, EnvironmentVariables } from '../types';
import DataLoaderError from '../../errors/DataLoaderError';
import capitalise from '../../utils/capitalise';
import { getAppEnv } from '../../../config/env';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import HtmlHeader from '../components/HtmlHeader';
import { getStaticPageLoadEvent } from '../../utils/omnitureUtils';

const queryString = require('query-string');

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

type StaticListIndexSingleProps = {
  data: StaticListIndexSingleData;
};

StaticListIndexSingle.fetchData = async function (_, match, urlObj): Promise<StaticListIndexSingleData> {
  // get pageType and letter from the
  let language = 'ENGLISH';
  let languageUrlKey = '';
  let languageCacheKey = '';
  if (String(urlObj.pathname).includes('staticPage/hi/')) {
    language = 'HINDI';
    languageUrlKey = 'hi/';
    languageCacheKey = 'HI_';
  }
  const [pageType /*ignored the letter 'by' so there is a coma next*/, , letter] = urlObj.pathname
    .match(/[A-Za-z]+-by-[A-Za-z]/g)[0]
    .split('-')
    .map((s) => s.toLowerCase());

  const pageSize = 300;
  const { page: pageOffset = 0 } = queryString.parse(urlObj.query);

  const apiString = {
    trains: 'getTrainsByStartingAlphabet',
    stations: 'getStationsByStartingAlphabet',
    routes: 'getRoutesByStartingAlphabet'
  };

  const urlString = `${DATA_LOADER_URL}/api/${apiString[pageType]}?index=${letter}&pageOffset=${pageOffset}&pageSize=${pageSize}&language=${language}`;

  let responseData;
  const response = await fetch(urlString);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new DataLoaderError(`Unable to fetch train list`, responseData.error);
  }

  const pathArray = urlObj.path.split('/');
  const cacheKey = 'HTML_STATIC_' + languageCacheKey + pathArray[pathArray.length - 1].toUpperCase();

  (responseData.template.analytics = getStaticPageLoadEvent('seo:rails:Railways:Index')),
    (responseData.template.language = language.toLowerCase());
  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    letter,
    pageType,
    pageOffset,
    pageSize,
    ...responseData,
    languageUrlKey
  };
};
function StaticListIndexSingle({ data }: StaticListIndexSingleProps) {
  const { languageUrlKey } = data;
  const alphabets = [...'abcdefghijklmnopqrstuvwxyz'];
  const urlPrefix = `/railways/${languageUrlKey}list-of${data.pageType !== 'stations' ? '-indian' : ''}${
    data.pageType === 'routes' ? `-train-` : `-railway-`
  }${data.pageType}`;
  return (
    <>
      <div className="container">
        <HtmlHeader data={data} containsAlternateHref={true} />
        <main>
          <div className="railSeoWrapper">
            <BreadCrumb breadcrumb={data.template.breadcrumb} />
            <StaticPageHeader title={data.template.h1_title} />
            <br />
            <div className="railSeoWrap__trainIndexLetters">
              {alphabets.map((letter) => (
                <a key={`${letter}`} href={`${urlPrefix}-by-${letter}.html`} className="primaryBtn">
                  {letter.toUpperCase()}
                </a>
              ))}
            </div>
            <br />
            <section>
              <div className="railSeoWrap__trainIndexSingleList">
                {data.pageType === 'trains' &&
                  data.trainsList.map((t) => {
                    return (
                      <div className="railSeoWrap__trainIndexSingleInfo" key={t.trainNumber}>
                        <a href={t.trainPageUrl}>
                          {t.trainName} - {t.trainNumber}
                        </a>
                      </div>
                    );
                  })}
                {data.pageType === 'stations' &&
                  data.stationsList.map((s) => {
                    return (
                      <div className="railSeoWrap__trainIndexSingleInfo" key={s.stationCode}>
                        <a href={s.stationPageUrl}>
                          {capitalise(s.stationName)} ({s.stationCode}), {s.city}
                        </a>
                      </div>
                    );
                  })}
                {data.pageType === 'routes' &&
                  data.routesList.map((r) => {
                    return (
                      <div className="railSeoWrap__trainIndexSingleInfo" key={r.source + '_' + r.destination}>
                        <a href={r.routePageUrl}>
                          {r.source} to {r.destination}
                        </a>
                      </div>
                    );
                  })}
              </div>
              <br />
              <Pagination
                currentPage={Number(data.pageOffset)}
                pageType={data.pageType}
                total={((): number => {
                  if (data.pageType === 'trains') return data.trainCount;
                  else if (data.pageType === 'stations') return data.stationsCount;
                  return data.routeCount;
                })()}
                letter={data.letter}
                pageSize={Number(data.pageSize)}
                languageUrlKey={languageUrlKey}
              />
            </section>
            <section className="railSeoWrap__stnDetails makeFlex spaceBetween">
              <RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.STATIC_LIST_SINGLE(data)} />
            </section>
          </div>
        </main>
      </div>
      <FooterSection template={data.template} />
    </>
  );
}

export default StaticListIndexSingle;
